"use client";
import React from "react";
import Link from "next/link";
import { Heart, Menu as MenuIcon, User as UserIcon } from "lucide-react";
import { Sheet, SheetTrigger, SheetContent, SheetTitle } from '@/themes/core/ui/sheet';
import { Menus } from "./Menus";
import { PhoneMenu } from "./PhoneMenu";
import { Search } from "./Search";
import { useWishlist, useSettings } from "@/themes/core/hooks";
import { getStoreMenusByIds } from "@/actions/navigation";
import { Menu } from "@/types/navigation";
import CartModal from "../../cart/CartModal";
import { Button } from '@/themes/core/ui/button';
import { CurrencySelector } from '../CurrencySelector';

interface HeaderProps {
  logo?: string | null;
  siteName?: string;
  menu?: { title: string; path: string }[];
  navbar_direction?: "auto" | "rtl" | "ltr";
  customMenus?: Menu[];
  basePath?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  showCart?: boolean;
  showWishlist?: boolean;
  enableCustomerAccounts?: boolean;
}

const Header = ({
  logo,
  siteName,
  menu,
  navbar_direction = 'auto',
  customMenus: injectedMenus = [],
  basePath,
  showSearch,
  searchPlaceholder,
  showCart,
  showWishlist,
  enableCustomerAccounts
}: HeaderProps) => {
  const { settings } = useSettings(); // Get settings
  const { items: wishlistItems, openWishlist } = useWishlist();
  const wishlistCount = wishlistItems.length;

  const [customMenus, setCustomMenus] = React.useState<Menu[]>([]);

  // Fetch Custom Menus
  // Fetch Custom Menus
  React.useEffect(() => {
    const fetchMenus = async () => {
      const headerConfig = (settings.header as any) || {};
      let ids: string[] = [];

      if (Array.isArray(headerConfig.menus)) {
        ids = headerConfig.menus;
      } else {
        ids = [
          headerConfig.menu_1_id,
          headerConfig.menu_2_id,
          headerConfig.menu_3_id,
          headerConfig.menu_4_id
        ].filter(Boolean);
      }

      // 1. If we have server-injected menus, filter them to only those that should be in the header
      if (injectedMenus && injectedMenus.length > 0) {
        const filtered = injectedMenus.filter(m => ids.includes(m.id) || ids.includes(m.handle));
        setCustomMenus(filtered);
        return;
      }

      // 2. Otherwise fetch them
      if (ids.length > 0) {
        try {
          const fetched = await getStoreMenusByIds(ids);
          const sorted = ids
            .map(id => fetched.find(m => m.id === id || m.handle === id))
            .filter((m): m is Menu => !!m);
          setCustomMenus(sorted);
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn("Failed to fetch menus", e);
          }
        }
      } else {
        setCustomMenus([]);
      }
    };

    fetchMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectedMenus, settings.header]);

  return (
    <header
      dir={navbar_direction === 'auto' ? undefined : navbar_direction}
      className="sticky top-0 z-40 w-full bg-primary text-primary-foreground border-b border-primary/10"
    >
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt={siteName || "Store"} className="h-8 w-auto object-contain brightness-0 invert" width={128} height={32} />
          ) : (
            <span className="text-xl font-bold tracking-tighter text-primary-foreground">{siteName || "Store"}</span>
          )}
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:block">
          {/* Desktop Menu */}
          <div className="hidden md:block">
            {/* If we have custom menus, we only take the first one and "unwrap" it effectively by passing it as the single source of truth 
                The Menus component will need to handle:
                1. If `customMenus` is passed, it usually iterates them.
                2. But we want ONE menu to be the source.
                
                Actually, simpler approach:
                Pass `customMenus` as array of 1.
                The logic inside `Menus` creates keys for each menu in `customMenus`. 
                If we want the items of that menu to be the top level, we need to change usage in `Menus.tsx`.
                
                For now, let's limit `customMenus` passed here to just the first one if it exists.
             */}
            {/* But we now support multiple menus being wrapped in "Folders" */}
            <Menus menu={menu} customMenus={customMenus} basePath={basePath} />
          </div>
        </div>

        {/* Mobile Menu & Actions */}
        <div className="flex items-center gap-1 md:gap-2">
          {(showSearch ?? true) && (
            <div className="block">
              <Search placeholder={searchPlaceholder} />
            </div>
          )}

          {/* Account */}
          {(enableCustomerAccounts ?? true) && (
            <Link
              href="/account"
              aria-label="الحساب"
              className="flex items-center justify-center h-10 w-10 hover:bg-primary-foreground/10 text-primary-foreground rounded-md transition-colors"
            >
              <UserIcon className="h-5 w-5" />
            </Link>
          )}

          {/* Currency Selector */}
          <CurrencySelector variant="dark" />

          {/* Wishlist */}
          {(showWishlist ?? true) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={openWishlist}
              aria-label="المفضلة"
              className="relative flex hover:bg-primary-foreground/10 text-primary-foreground hover:text-primary-foreground"
            >
              <Heart className="h-5 w-5" />
              {wishlistCount > 0 && <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />}
            </Button>
          )}

          {/* Cart */}
          {(showCart ?? true) && (
            <div className="[&_button]:text-primary-foreground [&_button]:hover:bg-primary-foreground/10 [&_.h-11]:h-10 [&_.w-11]:w-10 [&_.border-neutral-200]:border-none [&_.text-black]:text-primary-foreground [&_button]:rounded-md">
              <CartModal />
            </div>
          )}

          {/* Mobile Trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="فتح القائمة"
                className="md:hidden hover:bg-primary-foreground/10 text-primary-foreground hover:text-primary-foreground"
              >
                <MenuIcon className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side={navbar_direction === 'rtl' ? "right" : "left"}
              className={`w-[300px] p-0 ${navbar_direction === 'rtl' ? '[&>button]:left-4 [&>button]:right-auto' : ''}`}
            >
              <SheetTitle className="sr-only">قائمة الجوال</SheetTitle>
              <PhoneMenu
                menu={customMenus.length > 0 ? (function buildMenuTree(items: any[]): any[] {
                  const map = new Map<string, any>();
                  const roots: any[] = [];
                  items.forEach(item => {
                    map.set(item.id, { ...item, title: item.title, path: item.url || '#', children: [] });
                  });
                  items.forEach(item => {
                    const node = map.get(item.id)!;
                    const parentId = item.parent_id || item.parentId;
                    if (parentId && map.has(parentId)) {
                      map.get(parentId)!.children!.push(node);
                    } else { roots.push(node); }
                  });
                  return roots.sort((a, b) => (a.position || 0) - (b.position || 0));
                })(customMenus.flatMap(m => m.items || [])) : menu}
                direction={navbar_direction}
                basePath={basePath}
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export { Header };
